import React, { Fragment, forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface BubbleAccordionPropsBase {
  variant: 'navy' | 'velvet' | 'forest' | 'navy-light' | 'slate' | 'titanium' | 'turquoise';
  children: React.ReactNode;
  headerClassName?: string;
  singleMode?: boolean;
  noToggle?: boolean;
  noAnimation?: boolean;
  hideLocked?: boolean;
  bodyVariant?: BubbleAccordionPropsBase['variant'] | 'transparent';
  onOpen?: (indx: number) => void;
  headerFn?: (label: string, open: boolean, locked: boolean, indx: number) => React.ReactNode;
  labels?: string[];
  open?: number[];
  locked?: number[];
}

type BubbleAccordionProps = React.HTMLAttributes<HTMLDivElement> & BubbleAccordionPropsBase;

export interface BubbleAccordionRef {
  toggle(i: number): void;
}

// TODO: move tailwind classes to scss similar to other bubble components to remove the
// tie to tailwind
export default forwardRef<BubbleAccordionRef, BubbleAccordionProps>((props, ref) => {
  const {
    // from component
    variant,
    bodyVariant,
    labels,
    open: openProp,
    locked: lockedProp,
    hideLocked,
    singleMode,
    noToggle,
    noAnimation,
    headerFn,
    onOpen,
    headerClassName: propHeaderClasses,
    // from div
    className: propClasses,
    children,
    ...rest
  } = props;

  const [open, setOpen] = useState<boolean[]>([]);
  const [locked, setLocked] = useState<boolean[]>([]);
  const len = React.Children.count(children);

  useEffect(() => {
    if (len === open.length) return;

    const newOpen = new Array(len).fill(false);

    (openProp || []).forEach(idx => {
      if (idx > -1 && idx < newOpen.length) newOpen[idx] = true;
    });

    setOpen(newOpen);
  }, [children, openProp]);

  useEffect(() => {
    if (len === locked.length) return;

    const newLocked = new Array(len).fill(false);
    (lockedProp || []).forEach(idx => {
      if (idx > -1 && idx < newLocked.length) newLocked[idx] = true;
    });

    setLocked(newLocked);
  }, [children, lockedProp]);

  useImperativeHandle(ref, () => ({
    toggle: i => {
      if (!locked[i]) {
        toggleOpen(i);
      }
    },
  }));

  const classes = ['bubble-accordion'];

  if (propClasses) classes.push(propClasses);

  function toggleOpen(indx: number) {
    if (noToggle && open[indx]) return;

    const newOpen = [...open];

    newOpen[indx] = !newOpen[indx];

    if (singleMode) {
      newOpen.forEach((_, idx) => {
        if (idx !== indx && !locked[idx]) newOpen[idx] = false;
      });
    }

    setOpen(newOpen);

    if (props.onOpen) props.onOpen(indx);
  }

  let closedClass = '';
  let openClass = '';
  let hoverClass = '';
  let bgClass = '';
  let textClass = '';

  switch (variant) {
    case 'forest':
    case 'navy':
    case 'velvet':
    case 'titanium':
    case 'turquoise':
      closedClass = `bg-${variant}-darkest-2`;
      bgClass = `bg-${variant}-darkest`;
      hoverClass = `bg-${variant}-darker`;
      openClass = `bg-${variant}-dark`;
      textClass = 'text-white';
      break;
    case 'navy-light':
      closedClass = 'bg-navy';
      bgClass = 'bg-transparent';
      hoverClass = 'bg-navy-light';
      openClass = 'bg-navy-lighter';
      textClass = 'text-white';
      break;
    case 'slate':
      closedClass = 'bg-slate-lighter';
      bgClass = 'bg-transparent';
      hoverClass = 'bg-slate-light';
      openClass = 'bg-slate';
      textClass = 'text-white';
  }

  function constructHeaderClasses(indx: number) {
    const headerClasses = [`flex-shrink-0 ${textClass} py-2 px-4`];

    if (!open[indx]) headerClasses.push(`${closedClass}`);
    else headerClasses.push(`shadow-sm ${openClass}`);

    if (!locked[indx]) headerClasses.push('cursor-pointer');

    if (!open[indx] && !locked[indx]) {
      headerClasses.push(`hover:${hoverClass}`);
    }

    if (hideLocked && locked[indx]) headerClasses.push('hidden');

    return [...headerClasses, propHeaderClasses].join(' ');
  }

  const headerRenderFn =
    props.headerFn ||
    ((label: string, opn: boolean, lck: boolean) => (
      <div className="flex items-center">
        <span className="font-semibold">{label}</span>
        {!lck && <FontAwesomeIcon icon={opn ? 'chevron-up' : 'chevron-down'} className="ml-auto" />}
      </div>
    ));

  return (
    <div {...rest} className={classes.join(' ')}>
      {React.Children.map(children, (c, i) => (
        <Fragment key={i}>
          <div
            onClick={() => !locked[i] && toggleOpen(i)}
            className={constructHeaderClasses(i)}
            style={{
              minHeight: '40px',
              transition: noAnimation
                ? undefined
                : 'background-color 0.3s ease, border-radius 0.3s ease',
            }}
          >
            {headerRenderFn(labels ? labels[i] || '' : '', open[i], locked[i], i)}
          </div>
          <div
            style={{ transition: noAnimation ? undefined : 'max-height 0.3s ease' }}
            className={`flex-grow ${bgClass} relative ${!open[i] ? 'max-h-0' : 'max-h-full'}`}
          >
            <div
              className={`absolute top-0 left-0 w-full h-full ${
                !open[i] ? 'overflow-y-hidden' : 'overflow-y-auto'
              }`}
            >
              {c}
            </div>
          </div>
        </Fragment>
      ))}
    </div>
  );
});
